import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { api, SESSION_REVOKED_EVENT } from '../utils/apiClient';
import { SKIP_QA_SIGN_ON, QA_USER_ID, ENVIRONMENT } from '../config';
import { setSentryUser } from '../utils/sentry';
import { setNewRelicUser } from '../utils/newrelic';
import { setMixpanelUserProperties } from '../utils/analytics/setMixpanelUserProperties';
import { isExternalUrl, isPathwaysUrl, isValidReturnUrl } from '../utils/urlUtils';

async function getRedirectUrlWithToken(url: string): Promise<string | null> {
  if (!isPathwaysUrl(url)) {
    return url;
  }

  try {
    const response = await api.createAccessTokenForProduct('pathways');
    const token = response?.data?.attributes?.accessToken;
    
    if (token) {
      const urlObj = new URL(url);
      urlObj.searchParams.set('access', token);
      return urlObj.toString();
    }
  } catch (error) {
    console.error('Error generating access token for Pathways:', error);
  }
  
  return null;
}

// User type definition
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  preferredLocale?: string;
  preferredDateFormat?: string;
  trainingEventStartDate?: string;
  trainingEventEndDate?: string;
  trainingEventProgramTitle?: string;
  trainingEventInstructorSfid?: string;
  trainingEventTrialStartDate?: string;
  appUserCategory?: 'Program Trial' | 'Signup Trial' | 'Paid' | null;
  subscriptionCurrentPeriodStart?: string;
  subscriptionCurrentPeriodEnd?: string;
  subscriptionCreatedAt?: string;
  customerTypeId?: number;
  isLicenseUser?: boolean;
  billingCountry?: string;
  dashboardProducts?: any[];
  products?: any[];
  account?: any;
  licenseUser?: boolean;
  [key: string]: any; // Allow additional properties from transformUserData
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  masqueradedUser: string | null;
  login: (returnTo?: string, email?: string | null) => Promise<void>;
  logout: () => Promise<void>;
  handleMasqueradeCallback: (token: string) => Promise<void>;
  updateUser: (updates: any) => Promise<any>;
  refreshUser: () => Promise<User | null>;
  setMasqueradedUser: (userId: string | null) => void;
  startMasquerading: (targetUserId: string, token: string) => Promise<any>;
  stopMasquerading: () => Promise<void>;
  isMasquerading: () => boolean;
  getOriginalUserId: () => string | null;
  hasActiveSession: () => boolean;
  validateSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper function to determine if current route is public
const isPublicRoute = (pathname: string): boolean => {
  const publicRoutes = [
    '/register',
    '/registration_confirmation',
    '/unauthorized',
    '/prohibited',
    '/terms',
    '/auth/callback',
    '/auth/masquerade',
    '/signup',
    '/users/verify_recovery_email'
  ];

  return publicRoutes.some(route => pathname.startsWith(route));
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Always call useAuth0 hook (React hooks must be called unconditionally)
  // But only use its results when not in QA bypass mode
  const auth0Hook = useAuth0();
  const auth0 = SKIP_QA_SIGN_ON ? null : auth0Hook;

  // Use React Router navigation for SPA navigation (avoids full page reload)
  const navigate = useNavigate();

  const [state, setState] = useState({
    user: null as any,
    isAuthenticated: false,
    isLoading: true,
    error: null as string | null,
    masqueradedUser: null as string | null
  });

  // Track if we've created a backend session for the current Auth0 session
  const sessionCreatedRef = useRef(false);
  // Track if we're currently navigating (to prevent further processing)
  const isNavigatingRef = useRef(false);

  // When the backend reports the session as revoked (OIDC backchannel
  // logout, admin-forced logout, password change), drive a full Auth0
  // /v2/logout so the SSO cookie is cleared — otherwise the SPA will
  // silently re-authenticate and land the user right back in a new session.
  useEffect(() => {
    if (!auth0) return;

    const handler = () => {
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    };

    window.addEventListener(SESSION_REVOKED_EVENT, handler);
    return () => window.removeEventListener(SESSION_REVOKED_EVENT, handler);
  }, [auth0]);

  // Helper function to convert snake_case to camelCase
  const toCamelCase = (str: string): string => {
    return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
  };

  // Recursive function to transform all keys in an object from snake_case to camelCase
  const transformKeys = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
      return obj.map(transformKeys);
    }

    if (typeof obj === 'object') {
      const transformed: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        const camelKey = toCamelCase(key);
        transformed[camelKey] = transformKeys(value);
      }
      return transformed;
    }

    return obj;
  };

  const transformUserData = (userData: any, included: any[] = []) => {
    const attributes = userData.attributes || {};
    const relationships = userData.relationships || {};

    // Transform all attributes to camelCase recursively
    const transformedAttributes = transformKeys(attributes);

    // Parse ALL products from included data (for products field)
    const allProducts = included
      .filter((item: any) => item.type === 'product')
      .map((product: any) => ({
        id: product.id,
        ...transformKeys(product.attributes || {})
      }));

    // Get dashboard product IDs from relationships
    const dashboardProductIds = relationships.dashboard_products?.data?.map((item: any) => item.id) || [];

    // Filter products to get only dashboard products
    const dashboardProducts = allProducts.filter(product =>
      dashboardProductIds.includes(product.id)
    );

    // Parse account from included data
    const accountData = included.find((item: any) => item.type === 'account');
    const account = accountData ? {
      ...transformKeys(accountData.attributes || {})
    } : null;

    return {
      id: userData.id,
      ...transformedAttributes,
      // Override with specific defaults if needed
      preferredLocale: transformedAttributes.preferredLocale || 'en-US',
      preferredDateFormat: transformedAttributes.preferredDateFormat || 'MM-DD-YYYY',
      // Dashboard-specific fields
      dashboardProducts: dashboardProducts,
      products: allProducts,
      account: account,
      // License user check
      licenseUser: transformedAttributes.customerTypeId === 3,
    };
  };

  const loadQAUser = async () => {
    // Security check: Prevent QA user loading in production
    if (ENVIRONMENT === 'production') {
      console.error('SECURITY VIOLATION: Attempted to load QA user in production mode');
      throw new Error('QA user loading is not permitted in production');
    }

    try {
      const response = await api.getQAUser(QA_USER_ID);
      const userData = response.data?.data;

      if (userData) {
        const user = transformUserData(userData, response.data?.included);
        setSentryUser(user.id);
        setNewRelicUser(user.id);
        setMixpanelUserProperties(user);

        setState(prev => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false
        }));
      }
    } catch (error: any) {
      console.error('Failed to load QA user:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        isLoading: false
      }));
    }
  };

  // Create backend session after Auth0 authentication
  const createBackendSession = async (auth0Sub: string, email?: string, givenName?: string, familyName?: string) => {
    try {
      // Extract Auth0 session ID (sid) from ID token for backchannel logout support
      let auth0Sid: string | undefined;
      try {
        const claims = await auth0Hook.getIdTokenClaims();
        auth0Sid = claims?.sid;
      } catch {
        // Don't fail login if sid extraction fails
      }

      // Create secure session with backend
      await authService.createSecureSession(auth0Sub, email, givenName, familyName, auth0Sid);

      // Get full user data from backend
      const response = await api.getCurrentUser();
      const userData = response.data?.data;

      if (userData) {
        const user = transformUserData(userData, response.data?.included);

        // Check masquerading status
        let masqueradedUserId = null;
        try {
          const statusResponse = await api.getMasqueradingStatus();
          if (statusResponse.data?.is_masquerading) {
            masqueradedUserId = statusResponse.data.original_admin_user_id;
          }
        } catch (error) {
          // Ignore masquerading status check errors - not critical for initialization
          console.debug('Could not check masquerading status:', error);
        }

        setSentryUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
        setNewRelicUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
        setMixpanelUserProperties(user);

        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          masqueradedUser: masqueradedUserId
        });

        sessionCreatedRef.current = true;

        // Navigate to the return URL stored by onRedirectCallback, then clean up
        const returnTo = sessionStorage.getItem('auth0_return_to');
        sessionStorage.removeItem('auth0_return_to');

        // Only navigate if we have a stored returnTo OR we're on the auth callback page
        // Otherwise, let the user stay on their current page (e.g., when navigating from admin to /profile)
        const isOnAuthCallback = window.location.pathname === '/auth/callback';

        if (returnTo || isOnAuthCallback) {
          // Reject untrusted destinations (open-redirect guard) before navigating
          let destination: string | null =
            returnTo && isValidReturnUrl(returnTo) ? returnTo : '/';

          // Set navigating flag to prevent further auth processing
          isNavigatingRef.current = true;

          // Check if destination is external
          if (isExternalUrl(destination)) {
            destination = await getRedirectUrlWithToken(destination);
            if (destination) {
              window.location.href = destination;
            } else {
              navigate('/', { viewTransition: true });
            }
          } else {
            // Use React Router navigate with view transitions for smooth SPA navigation
            navigate(destination, { viewTransition: true });
          }
        }

        return user;
      }
    } catch (error: any) {
      console.error('Failed to create backend session:', error);

      // Handle specific error responses
      if (error.response?.data?.error === 'user_not_found' && error.response.data.redirect_url) {
        // SSO user not pre-provisioned - redirect to prohibited page
        window.location.href = error.response.data.redirect_url;
        return null;
      }

      throw error;
    }
    return null;
  };

  // Initialize authentication
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip if we're already navigating away
      if (isNavigatingRef.current) {
        return;
      }

      try {
        const isOnPublicRoute = isPublicRoute(window.location.pathname);
        const isOnAuthCallback = window.location.pathname === '/auth/callback';

        // Check if QA mode is enabled - with additional production safety check
        if (SKIP_QA_SIGN_ON && QA_USER_ID) {
          // Additional runtime safety check to prevent production bypass
          if (ENVIRONMENT === 'production') {
            console.error('SECURITY VIOLATION: QA authentication bypass attempted in production mode');
            throw new Error('QA authentication bypass is not allowed in production');
          }

          // An active masquerade session takes priority over the QA bypass
          // user. Without this check, every AuthProvider remount (e.g. a
          // hard reload/deep link while on /kaiya) silently snaps back to
          // VITE_QA_USER_ID and clobbers the masqueraded identity, even
          // though the real prosci_session cookie still holds the
          // masqueraded user.
          let masqueradedUserId: string | null = null;
          let isMasquerading = false;
          try {
            const statusResponse = await api.getMasqueradingStatus();
            isMasquerading = !!statusResponse.data?.is_masquerading;
            masqueradedUserId = statusResponse.data?.original_admin_user_id ?? null;
          } catch (error) {
            console.debug('Could not check masquerading status:', error);
          }

          if (isMasquerading) {
            const response = await api.getCurrentUser();
            const userData = response.data?.data;

            if (userData) {
              const user = transformUserData(userData, response.data?.included);

              setSentryUser(user.id, { masquerading: true, originalUser: masqueradedUserId });
              setNewRelicUser(user.id, { masquerading: true, originalUser: masqueradedUserId });
              setMixpanelUserProperties(user);

              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                masqueradedUser: masqueradedUserId
              });
              sessionCreatedRef.current = true;
              return;
            }
          }

          await loadQAUser();
          return;
        }

        // Wait for Auth0 to initialize
        if (auth0?.isLoading) {
          return; // Will re-run when auth0.isLoading changes
        }

        // If Auth0 authenticated, ensure we have a backend session
        // This must run even on public routes like /auth/callback to complete the login flow
        if (auth0?.isAuthenticated && auth0?.user?.sub) {
          // Check if we already have a valid backend session
          const hasSession = await authService.validateSession();

          if (hasSession && !sessionCreatedRef.current) {
            // Session exists, load user data
            const response = await api.getCurrentUser();
            const userData = response.data?.data;

            if (userData) {
              const user = transformUserData(userData, response.data?.included);

              // Check masquerading status
              let masqueradedUserId = null;
              try {
                const statusResponse = await api.getMasqueradingStatus();
                if (statusResponse.data?.is_masquerading) {
                  masqueradedUserId = statusResponse.data.original_admin_user_id;
                }
              } catch (error) {
                console.debug('Could not check masquerading status:', error);
              }

              setSentryUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
              setNewRelicUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
              setMixpanelUserProperties(user);

              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                masqueradedUser: masqueradedUserId
              });

              sessionCreatedRef.current = true;

              // Navigate to the return URL if coming from callback, or to dashboard if on callback page
              let returnTo: string | null = sessionStorage.getItem('auth0_return_to');
              sessionStorage.removeItem('auth0_return_to');

              // Reject untrusted destinations (open-redirect guard) before navigating
              if (returnTo && !isValidReturnUrl(returnTo)) {
                returnTo = '/';
              }

              if (returnTo) {
                isNavigatingRef.current = true;
                if (isExternalUrl(returnTo)) {
                  returnTo = await getRedirectUrlWithToken(returnTo);
                  if (returnTo) {
                    window.location.href = returnTo;
                  } else {
                    navigate('/', { viewTransition: true });
                  }
                } else {
                  navigate(returnTo, { viewTransition: true });
                }
              } else if (isOnAuthCallback) {
                // If we're on /auth/callback with an existing session but no returnTo, go to dashboard
                isNavigatingRef.current = true;
                navigate('/', { viewTransition: true });
              }
            } else {
              setState(prev => ({ ...prev, isLoading: false }));
            }
          } else if (!sessionCreatedRef.current) {
            // No backend session, create one
            // Pass email with fallback to name (some SAML connections put email in name field)
            await createBackendSession(
              auth0.user.sub,
              auth0.user.email || auth0.user.name,
              auth0.user.given_name,
              auth0.user.family_name
            );
          }
        } else if (auth0 && !auth0.isLoading && !auth0.isAuthenticated) {
          // Auth0 not authenticated - but we might have a valid backend session
          // This happens after full page navigation since Auth0 uses memory cache
          const hasSession = await authService.validateSession();

          if (hasSession && !sessionCreatedRef.current) {
            // We have a valid backend session even though Auth0 forgot about us
            const response = await api.getCurrentUser();
            const userData = response.data?.data;

            if (userData) {
              const user = transformUserData(userData, response.data?.included);

              // Check masquerading status
              let masqueradedUserId = null;
              try {
                const statusResponse = await api.getMasqueradingStatus();
                if (statusResponse.data?.is_masquerading) {
                  masqueradedUserId = statusResponse.data.original_admin_user_id;
                }
              } catch (error) {
                console.debug('Could not check masquerading status:', error);
              }

              setSentryUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
              setNewRelicUser(user.id, masqueradedUserId ? { masquerading: true, originalUser: masqueradedUserId } : undefined);
              setMixpanelUserProperties(user);

              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                masqueradedUser: masqueradedUserId
              });

              sessionCreatedRef.current = true;

              // If on auth callback page, navigate to dashboard
              if (isOnAuthCallback) {
                isNavigatingRef.current = true;
                navigate('/', { viewTransition: true });
              }
            } else {
              setState(prev => ({ ...prev, isLoading: false }));
            }
          } else {
            // No backend session either - user is truly not authenticated
            setState(prev => ({ ...prev, isLoading: false }));
          }
        }
      } catch (error: any) {
        console.error('Auth initialization error:', error);
        setState(prev => ({
          ...prev,
          error: error.message,
          isLoading: false
        }));
      }
    };

    initializeAuth();
  }, [auth0?.isLoading, auth0?.isAuthenticated, auth0?.user?.sub]);

  // Session validation (runs periodically)
  useEffect(() => {
    if (state.isAuthenticated && !SKIP_QA_SIGN_ON && !isPublicRoute(window.location.pathname)) {
      const interval = setInterval(async () => {
        const valid = await authService.validateSession();
        if (!valid) {
          logout();
        }
      }, 30 * 60 * 1000); // Every 30 minutes

      return () => clearInterval(interval);
    }
  }, [state.isAuthenticated]);

  const login = useCallback(async (returnTo?: string, email: string | null = null) => {
    if (SKIP_QA_SIGN_ON) {
      console.warn('Login called in QA bypass mode');
      return;
    }

    try {
      await auth0?.loginWithRedirect({
        appState: { returnTo: returnTo || window.location.pathname },
        authorizationParams: email ? { login_hint: email } : undefined
      });
    } catch (error: any) {
      console.error('Login failed:', error);
      setState(prev => ({
        ...prev,
        error: error.message,
        isLoading: false
      }));
    }
  }, [auth0]);

  const logout = useCallback(async () => {
    // Reset session tracking
    sessionCreatedRef.current = false;

    // Destroy backend session
    await authService.destroySession();

    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      masqueradedUser: null
    });

    // If masquerading, redirect to admin
    if (state.masqueradedUser) {
      window.location.href = `${import.meta.env.VITE_ADMIN_URL}/admin/customers/stop_masquerading`;
    } else if (!SKIP_QA_SIGN_ON && auth0) {
      // Logout from Auth0
      auth0.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    }
  }, [state.masqueradedUser, auth0]);

  // Handle masquerade callback - exchange token for session
  const handleMasqueradeCallback = useCallback(async (token: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Exchange the masquerade token for a session
      const response = await api.exchangeMasqueradeToken(token);

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to exchange masquerade token');
      }

      const userData = response.data.user?.data;
      const includedData = response.data.user?.included || [];
      const originalAdminUserId = response.data.original_admin_user_id;

      // Transform user data with included resources (same as getCurrentUser flow)
      const user = transformUserData(userData, includedData);

      setSentryUser(user.id, { masquerading: true, originalUser: originalAdminUserId });
      setNewRelicUser(user.id, { masquerading: true, originalUser: originalAdminUserId });

      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        masqueradedUser: originalAdminUserId
      });

      // Redirect to dashboard using SPA navigation
      navigate('/', { viewTransition: true });
    } catch (error: any) {
      console.error('Masquerade callback error:', error);
      setState(prev => ({
        ...prev,
        error: error.response?.data?.error || error.message || 'Masquerade authentication failed',
        isLoading: false
      }));
      throw error;
    }
  }, [navigate]);

  const updateUser = useCallback(async (updates: any) => {
    if (!state.user) {
      throw new Error('No user to update');
    }

    try {
      const response = await api.updateUser(state.user.id, updates);
      const updatedUser = transformUserData(response.data?.data);

      setState(prev => ({
        ...prev,
        user: updatedUser
      }));

      return updatedUser;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  }, [state.user]);

  // Refresh user data from the backend (useful after email verification or other external changes)
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.getCurrentUser();
      const userData = response.data?.data;

      if (userData) {
        const user = transformUserData(userData, response.data?.included);

        setSentryUser(user.id, state.masqueradedUser ? { masquerading: true, originalUser: state.masqueradedUser } : undefined);
        setNewRelicUser(user.id, state.masqueradedUser ? { masquerading: true, originalUser: state.masqueradedUser } : undefined);
        setMixpanelUserProperties(user);

        setState(prev => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false,
        }));

        return user;
      }
      return null;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }, [state.masqueradedUser]);

  const setMasqueradedUser = useCallback((userId: string | null) => {
    setState(prev => ({
      ...prev,
      masqueradedUser: userId
    }));
  }, []);

  // Start masquerading as another user (admin feature)
  const startMasquerading = useCallback(async (targetUserId: string, token: string) => {
    if (!targetUserId || !token) {
      throw new Error('User ID and admin token are required for masquerading');
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }));

      // Call the Connect API masquerade endpoint
      const response = await api.startMasquerading(targetUserId, token);
      const masqueradedUserData = transformUserData(response.data.user.data, response.data.user.included || []);

      // Store the original admin user ID from current state
      const originalUserId = state.user?.id;

      setState(prev => ({
        ...prev,
        user: masqueradedUserData,
        masqueradedUser: originalUserId,
        isLoading: false
      }));

      // Set context for monitoring
      setSentryUser(masqueradedUserData.id, { masquerading: true, originalUser: originalUserId });
      setNewRelicUser(masqueradedUserData.id, { masquerading: true, originalUser: originalUserId });

      return masqueradedUserData;
    } catch (error) {
      console.error('Failed to start masquerading:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to start masquerading',
        isLoading: false
      }));
      throw error;
    }
  }, [state.user]);

  // Stop masquerading and redirect to admin app
  const stopMasquerading = useCallback(async () => {
    if (!state.masqueradedUser) {
      console.warn('Not currently masquerading');
      return;
    }

    const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';

    try {
      // Call API to stop masquerading - backend restores the original admin session
      // and sets a new session cookie with the admin's credentials
      const response = await api.stopMasquerading();

      // Use the redirect URL from the response, or default to admin users page
      // Response is wrapped in { data: ... } by the apiClient
      const redirectPath = response.data?.redirect_url;
      const redirectUrl = redirectPath
        ? `${adminUrl}${redirectPath}`
        : `${adminUrl}/users`;

      // Redirect to admin - the backend has already set the restored admin session cookie
      window.location.href = redirectUrl;
    } catch (err) {
      console.warn('Stop masquerading failed:', err);
      // Still redirect even if API fails
      window.location.href = `${adminUrl}/users`;
    }
  }, [state.masqueradedUser]);

  // Check if current user is masquerading
  const isMasquerading = useCallback(() => {
    return !!state.masqueradedUser;
  }, [state.masqueradedUser]);

  // Get original admin user ID (when masquerading)
  const getOriginalUserId = useCallback(() => {
    return state.masqueradedUser;
  }, [state.masqueradedUser]);

  const value: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    masqueradedUser: state.masqueradedUser,
    login,
    logout,
    handleMasqueradeCallback,
    updateUser,
    refreshUser,
    setMasqueradedUser,
    startMasquerading,
    stopMasquerading,
    isMasquerading,
    getOriginalUserId,
    // Secure session methods
    hasActiveSession: authService.hasActiveSession.bind(authService),
    validateSession: authService.validateSession.bind(authService)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
